import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { isMobileDevice } from 'common/utilities/utils';
import CartHeaderItem from './ReusableComponents/CartHeaderItem';
import { UserOrderContext } from 'context/context';
import FaqComponent from './ReusableComponents/FaqComponent';
import { checkForSegmentedHelpData, getIndulgentAddOnContainerSize } from '../Utils/cartPageUtils';
import { CART_PAGE_CONSTANTS } from 'organisms/CartPage/Constants/CartPageConstants';
import FoodItemList from './ReusableComponents/FoodItemList';
import CartSummaryCard from './ReusableComponents/CartSummaryCard';
import IndulgentAddOn from './ReusableComponents/IndulgentAddOn';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';
import ImageComponent from 'atoms/Image';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';

const CartPageItems = props => {
  const {
    contentData = {},
    currencySymbol = '',
    currencyConversionFactor = '',
    addHope = '',
    carryBag = '',
    confirmChangesHandler,
    tenantId = '',
    maxItemCountPerLineItem,
    faqAndContactData = {},
    dispositionData = {},
    isModelClosed = false,
    isShowCreateOrderError,
    isRemoveItemLoading = false,
  } = props;
  const { userOrderState, userOrderStateDispatch } = useContext(UserOrderContext);
  const { cartList = [], cartSummary = {}, bucketId = '', showTaxLines } = contentData;
  const [isMobile, setMobileHeader] = useState(isMobileDevice(window.innerWidth));
  const userParams = useSelector(state => state?.appStateReducer?.userStatus);
  const storesReducer = useSelector(data => data?.storesReducer);
  const { storesWithStatus } = storesReducer || {};
  const { loggedIn } = userParams || '';
  const [openIndulgentModal, setOpenIndulgentModal] = useState(false);
  const [imageContainerSize, setImageContainerSize] = useState({ containerHeight: 0, containerWidth: 0 });
  const { removeItemCart } = userOrderState;
  const { REMOVE_ITEM_FLAG, INDULGENT_ADD_ON } = CART_PAGE_CONSTANTS;
  const { HEADER, CONTINUE_TO_MY_CART } = INDULGENT_ADD_ON;
  const { ITEM_ADDED_TO_CART, ADDON_ADD_TO_CART } = TRANSLATE_MAPPING_KEY;

  useEffect(() => {
    setOpenIndulgentModal(true);
    windowResized();
    userOrderStateDispatch({ type: REMOVE_ITEM_FLAG, value: { removeItemCart: false } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setOpenIndulgentModal(false);
  };

  const windowResized = () => {
    const isMobileView = isMobileDevice(window.innerWidth);
    setMobileHeader(isMobileView);
    const indulgentImageSize = getIndulgentAddOnContainerSize(isMobileView, window.innerWidth);
    const { containerHeight, containerWidth } = indulgentImageSize;
    setImageContainerSize({ containerHeight, containerWidth });
  };

  useEffect(() => {
    window.addEventListener('resize', windowResized);
  }, [isMobile]);

  const indulgentModal = () => {
    return imageContainerSize.containerHeight ? (
      <IndulgentAddOn
        isMobile={isMobile}
        openIndulgentModal={openIndulgentModal}
        handleClose={handleCloseModal}
        removeItemCart={removeItemCart}
        IndulgentData={cartSummary.recommendedItems}
        imageContainerSize={imageContainerSize}
        modalTitle={HEADER}
        continueButtonText={CONTINUE_TO_MY_CART}
        toastMassage={ITEM_ADDED_TO_CART}
        addToCart={ADDON_ADD_TO_CART}
      />
    ) : (
      <></>
    );
  };

  const desktopCart = () => {
    return (
      <div className='cart-page-container-desktop grid-container' data-testid='cart-page-desktop-test'>
        <div className='cart-header-container-desktop' isMobile={false}>
          <CartHeaderItem />
          {isShowCreateOrderError && (
            <div className='row row-cols-12 mt-10px payment-failed-error-box'>
              <ImageComponent srcFile={IMAGE_PATH.Error} className='mt-3px mr-5px' />
              <div className='ml-10px'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.ERROR_MESSAGE_IN_ACTIVE)}</div>
            </div>
          )}
        </div>
        <div className='cart-content-container-desktop'>
          <div className='cart-content-foodItemList'>
            <FoodItemList
              contentData={cartList}
              isMobile={false}
              currencySymbol={currencySymbol}
              addHope={addHope}
              carryBag={carryBag}
              maxItemCountPerLineItem={maxItemCountPerLineItem}
              cartSummary={cartSummary}
              bucketId={bucketId}
              loggedIn={loggedIn}
              storesWithStatus={storesWithStatus}
              isRemoveItemLoading={isRemoveItemLoading}
            />
          </div>
          <div className='cart-summary-card-desktop'>
            <CartSummaryCard
              contentData={cartSummary}
              currencySymbol={currencySymbol}
              currencyConversionFactor={currencyConversionFactor}
              addHope={addHope}
              carryBag={carryBag}
              isMobile={false}
              confirmChangesHandler={confirmChangesHandler}
              tenantId={tenantId}
              bucketId={bucketId}
              dispositionData={dispositionData}
              isModelClosed={isModelClosed}
              loggedIn={loggedIn}
              storesWithStatus={storesWithStatus}
              cartList={cartList}
              showTaxLines={showTaxLines}
              history={props.history}
            />
          </div>
        </div>
        {cartSummary?.recommendedItems?.length > 0 && (
          <div className='cart-addon-container-desktop'>{indulgentModal()}</div>
        )}
        {checkForSegmentedHelpData(faqAndContactData) && (
          <div className='cart-faq-container-desktop'>
            <FaqComponent faqAndContactData={faqAndContactData} isMobile={false} history={props.history} />
          </div>
        )}
      </div>
    );
  };

  const mobileCart = () => {
    return (
      <div className='cart-page-container-mobile' data-testid='cart-page-mobile-test'>
        <div className='cart-header-container-mobile' isMobile={true}>
          <CartHeaderItem />
          {isShowCreateOrderError && (
            <div className='row row-cols-12 mt-10px payment-failed-error-box'>
              <ImageComponent srcFile={IMAGE_PATH.Error} className='mt-3px mr-5px' />
              <div className='ml-10px'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.ERROR_MESSAGE_IN_ACTIVE)}</div>
            </div>
          )}
        </div>
        <div className='cart-content-container-mobile'>
          <FoodItemList
            contentData={cartList}
            isMobile={true}
            currencySymbol={currencySymbol}
            addHope={addHope}
            carryBag={carryBag}
            maxItemCountPerLineItem={maxItemCountPerLineItem}
            cartSummary={cartSummary}
            bucketId={bucketId}
            loggedIn={loggedIn}
            storesWithStatus={storesWithStatus}
            isRemoveItemLoading={isRemoveItemLoading}
          />
        </div>
        {cartSummary?.recommendedItems?.length > 0 && (
          <div className='cart-addon-container-mobile'>{indulgentModal()}</div>
        )}

        <div className='cart-summary-card-mobile'>
          <CartSummaryCard
            contentData={cartSummary}
            currencySymbol={currencySymbol}
            currencyConversionFactor={currencyConversionFactor}
            addHope={addHope}
            carryBag={carryBag}
            isMobile={true}
            confirmChangesHandler={confirmChangesHandler}
            tenantId={tenantId}
            bucketId={bucketId}
            dispositionData={dispositionData}
            isModelClosed={isModelClosed}
            loggedIn={loggedIn}
            storesWithStatus={storesWithStatus}
            cartList={cartList}
            showTaxLines={showTaxLines}
            history={props.history}
          />
        </div>
        {checkForSegmentedHelpData(faqAndContactData) && (
          <div className='cart-faq-container-mobile'>
            <FaqComponent faqAndContactData={faqAndContactData} isMobile={true} history={props.history} />
          </div>
        )}
      </div>
    );
  };
  return isMobile ? mobileCart() : desktopCart();
};

export default CartPageItems;
